import React from 'react';
import { withRouter, Link, Prompt } from 'react-router-dom';
import { Container, Tabs, Tab } from 'react-bootstrap';

// Services & Helpers
import BaseService from 'services/BaseService';
import GlobalStateService from 'services/GlobalStateService';
import SubAgencyService from 'services/SubAgencyService';
import LocationService from 'services/LocationService';
import BootboxHelper from 'helpers/BootboxHelper';

// Components
import Loader from 'components/common/Loader';
import PageHeader from 'components/common/PageHeader';
import BasicDetails from 'components/admin/sub-agencies/BasicDetails';
import Instructors from 'components/admin/sub-agencies/Instructors';
import Users from 'components/admin/sub-agencies/Users';
import ContractModal from 'components/admin/contract/ContractModal';

// Images
import silhouetteImage from 'images/sub-agency-silhouette.png';

//-------------------------------------------------------------------------------------------------------------------

class SubAgencyViewPage extends React.Component {

    constructor(props) {
        super(props);

        this.contractModalRef = React.createRef();

        this.state = {
            isLoading: true,
            tab: 'basic-details'
        };
    }

    //-------------------------------------------------------------------------------------------------------------------
    //  React lifecycle
    //-------------------------------------------------------------------------------------------------------------------

    componentDidMount() {
        window.scrollTo(0, 0);
        this.load();
    }

    componentDidUpdate(oldProps) {
        if (this.props.match.params.slug != oldProps.match.params.slug) {
            this.load();
        }
    }

    //-------------------------------------------------------------------------------------------------------------------
    //  General
    //-------------------------------------------------------------------------------------------------------------------

    async load(isSilent) {
        GlobalStateService.setValues({
            area: 'sub-agencies'
        });
        if (!isSilent) {
            this.setState({ isLoading: true });
        }
        this.setState({ isChanged: false, isBusy: false });
        const slug = this.props.match.params.slug;
        const loginInfo = GlobalStateService.getValue('loginInfo');

        // Load related entities
        const countries = await LocationService.listCountries();
        const geos = await LocationService.listGeos();

        // Load sub-agency
        let subAgency;
        if (slug == 'new') {
            subAgency = {
                geoIDs: {}
            };

            // Set default geo
            geos.map(g => {
                if (g.isDefault) {
                    subAgency.geoIDs[g.id] = true;
                }
            });
        } else {
            subAgency = await BaseService.callAPI('sub-agency/get-basic-details/' + slug);
        }

        // Update UI
        this.setState({
            subAgency,
            countries,
            geos,
            loginInfo,
            isLoading: false
        });
    }

    updateFields(values) {
        const subAgency = { ...this.state.subAgency };
        for (var fieldName in values) {
            const value = values[fieldName];
            subAgency[fieldName] = value;
        }
        this.setState({ subAgency });
    }

    async confirmDelete() {
        const confirm = await BootboxHelper.confirm('Are you sure you want to delete this sub-agency?');
        if (confirm) {
            this.delete();
        }
    }

    async delete() {
        const { subAgency } = this.state;
        this.setState({ isLoading: true });
        await SubAgencyService.delete(subAgency.id);
        this.props.history.push('/sub-agencies');
    }

    async uploadPhoto(field) {
        const { subAgency } = this.state;
        if (field.files.length > 0) {
            const file = field.files[0];
            await SubAgencyService.uploadPhoto(subAgency.id, file);
            field.value = '';
            this.updateFields({
                photoVersion: subAgency.photoVersion + 1
            });
        }
    }

    async viewContract(id) {
        await this.contractModalRef.current.show({ id });
    }
    
    //-------------------------------------------------------------------------------------------------------------------

    render() {
        const {
            isLoading,
            subAgency,
            countries,
            geos,
            isChanged,
            onSave,
            isBusy
        } = this.state;
        const { history } = this.props;
        const tab = this.props.match.params.tab || 'basic-details';

        if (isLoading) {
            return (<Loader />);
        }
        const loginInfo = GlobalStateService.getValue('loginInfo');
        
        setTimeout(() => {
            this.autoFocus = null;
        }, 0);

        const tabProps = {
            subAgency,
            isEditMode: true,
            history,
            slug: subAgency.slug,
            loginInfo,
            countries,
            geos,
            onUpdate: (subAgency) => this.setState({ subAgency }),
            setIsChanged: (isChanged, onSave) => {
                window.onbeforeunload = (isChanged ?
                    () => 'You have unsaved changes. Are you sure you want to move away from this page?' :
                    () => null
                );
                this.setState({ isChanged, onSave });
            },
            setIsBusy: (isBusy) => this.setState({ isBusy })
        };

        return (<>

            <Prompt
                when={!!isChanged}
                message={location => 'You have unsaved changes. Are you sure you want to move away from this page?'}
            />

            <div className="page sub-agency-view-page">

                <PageHeader>
                    {this.renderProfilePhoto()}

                    <h1>
                        {subAgency.name ?
                            <>{subAgency.name}</> :
                            <>New sub-agency</>
                        }
                    </h1>

                    {!!subAgency.id &&
                        <div className="buttons">

                            {this.renderActionsButton()}
                            
                        </div>
                    }

                </PageHeader>

                <main>

                    <Container>

                        <Tabs
                            activeKey={tab}
                            onSelect={tab => {
                                const url = `/sub-agency/${subAgency.slug}/${tab}`;
                                this.props.history.replace(url);
                            }}
                        >
                            <Tab eventKey="basic-details" title="Basic Details">
                                {tab == 'basic-details' &&
                                    <BasicDetails {...tabProps} />
                                }
                            </Tab>
                            {!!subAgency.id &&
                                <Tab eventKey="instructors" title="Instructors">
                                    {tab == 'instructors' &&
                                        <Instructors {...tabProps} />
                                    }
                                </Tab>
                            }
                            {!!subAgency.id &&
                                <Tab eventKey="users" title="Manager users">
                                    {tab == 'users' &&
                                        <Users {...tabProps} />
                                    }
                                </Tab>
                            }
                        </Tabs>

                    </Container>

                </main>

            </div>

            {isChanged && !isBusy &&
                <button type="button" className="btn btn-primary floating-save-button" onClick={onSave}>
                    <span className="fa-solid fa-check" />{' '}
                    Save changes
                </button>
            }

            <ContractModal ref={this.contractModalRef} />

        </>);
    }

    renderProfilePhoto() {
        const { subAgency } = this.state;

        return (
            <label className="profile-photo upload-button selectable" style={{
                backgroundImage: (
                    subAgency.photoVersion ? `url(/api/sub-agency/get-photo/${subAgency.id}?size=Medium&v=${subAgency.photoVersion})` :
                        `url(${silhouetteImage})`
                )
            }}>
                {subAgency.id && <>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={e => this.uploadPhoto(e.target)}
                    />
                    <div className="edit-icon">
                        <span className="fa-solid fa-pencil-alt"></span>
                    </div>
                </>}
            </label>
        );
    }

    renderActionsButton() {
        const { subAgency } = this.state;
        const loginInfo = GlobalStateService.getValue('loginInfo');

        if (loginInfo.isAdmin) {
            return (
                <div className="dropdown">
                    <button className="btn btn-primary dropdown-toggle" type="button" id="actions-menu" data-bs-toggle="dropdown" aria-expanded="false">
                        <span className="d-none d-sm-inline">Actions</span>
                    </button>
                    <ul className="dropdown-menu" aria-labelledby="actions-menu">
                        {!!subAgency.latestContractID &&
                            <li>
                                <Link className="dropdown-item" to="#" onClick={e => {
                                    e.preventDefault();
                                    this.viewContract(subAgency.latestContractID);
                                }}>
                                    <span className="fa-solid fa-handshake"></span>{' '}
                                    Contract
                                </Link>
                            </li>
                        }
                        
                        {!subAgency.id &&
                            <li>
                                <Link className="dropdown-item" to="#" onClick={e => {
                                    e.preventDefault();
                                    this.save();
                                }}>
                                    <span className="fa-solid fa-save"></span>{' '}
                                    Save
                                </Link>
                            </li>
                        }
                        <li>
                            <a className="dropdown-item" href="#" onClick={e => {
                                e.preventDefault();
                                this.confirmDelete();
                            }}>
                                <span className="fa-solid fa-trash"></span>
                                {' '}
                                Delete
                            </a>
                        </li>
                    </ul>
                </div>
            );
        }
    }

}

export default withRouter(SubAgencyViewPage);